<script>
/* eslint-disable vue/no-unused-properties, vue/no-unused-emit-declarations */
import { ref } from 'vue';
import { REPLY_EDITOR_MODES } from './constants';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import QRCode from 'qrcode';
import FileUpload from 'vue-upload-component';
import * as ActiveStorage from 'activestorage';
import inboxMixin from 'shared/mixins/inboxMixin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { getAllowedFileTypesByChannel } from '@chatwoot/utils';
import { ALLOWED_FILE_TYPES } from 'shared/constants/messages';
import ConversationApi from 'dashboard/api/inbox/conversation';
import MessageApi from 'dashboard/api/inbox/message';
import { useAlert, useTrack } from 'dashboard/composables';
import ConversationParticipant from 'dashboard/routes/dashboard/conversation/ConversationParticipant.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { mapGetters } from 'vuex';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

export default {
  name: 'ReplyBottomPanel',
  components: {
    FileUpload,
    ConversationParticipant,
  },
  mixins: [inboxMixin],
  props: {
    isNote: {
      type: Boolean,
      default: false,
    },
    onSend: {
      type: Function,
      default: () => {},
    },
    sendButtonText: {
      type: String,
      default: '',
    },
    recordingAudioDurationText: {
      type: String,
      default: '00:00',
    },
    // inbox prop is used in /mixins/inboxMixin,
    // remove this props when refactoring to composable if not needed
    // eslint-disable-next-line vue/no-unused-properties
    inbox: {
      type: Object,
      default: () => ({}),
    },
    showFileUpload: {
      type: Boolean,
      default: false,
    },
    showAudioRecorder: {
      type: Boolean,
      default: false,
    },
    onFileUpload: {
      type: Function,
      default: () => {},
    },
    toggleEmojiPicker: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorder: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorderPlayPause: {
      type: Function,
      default: () => {},
    },
    isRecordingAudio: {
      type: Boolean,
      default: false,
    },
    recordingAudioState: {
      type: String,
      default: '',
    },
    isSendDisabled: {
      type: Boolean,
      default: false,
    },
    isOnPrivateNote: {
      type: Boolean,
      default: false,
    },
    enableMultipleFileUpload: {
      type: Boolean,
      default: true,
    },
    enableWhatsAppTemplates: {
      type: Boolean,
      default: false,
    },
    enableContentTemplates: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    message: {
      type: String,
      default: '',
    },
    newConversationModalActive: {
      type: Boolean,
      default: false,
    },
    portalSlug: {
      type: String,
      required: true,
    },
    conversationType: {
      type: String,
      default: '',
    },
    showQuotedReplyToggle: {
      type: Boolean,
      default: false,
    },
    quotedReplyEnabled: {
      type: Boolean,
      default: false,
    },
    replyMode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
  },
  emits: [
    'replaceText',
    'toggleInsertArticle',
    'selectWhatsappTemplate',
    'selectContentTemplate',
    'toggleQuotedReply',
    'setReplyMode',
    'suggestReply',
  ],
  setup() {
    const { setSignatureFlagForInbox, fetchSignatureFlagFromUISettings } =
      useUISettings();

    const uploadRef = ref(false);

    const keyboardEvents = {
      '$mod+Alt+KeyA': {
        action: () => {
          // TODO: This is really hacky, we need to replace the file picker component with
          // a custom one, where the logic and the component markup is isolated.
          // Once we have the custom component, we can remove the hacky logic below.

          const uploadTriggerButton = document.querySelector(
            '#conversationAttachment'
          );
          uploadTriggerButton.click();
        },
        allowOnFocusedInput: true,
      },
    };

    useKeyboardEvents(keyboardEvents);

    return {
      setSignatureFlagForInbox,
      fetchSignatureFlagFromUISettings,
      uploadRef,
    };
  },
  data() {
    return {
      ALLOWED_FILE_TYPES,
      isMobileUploadModalOpen: false,
      isFetchingMobileUploadLink: false,
      isFetchingMobileUploadMedia: false,
      isAttachingMobileUploadMedia: false,
      mobileUploadUrl: '',
      mobileUploadQrCodeUrl: '',
      mobileUploadMedia: [],
      mobileUploadFavorites: [],
      mobileUploadGalleryTab: 'recent',
      isComposerMenuOpen: false,
      isTargetMenuOpen: false,
      executingMacroId: null,
      REPLY_EDITOR_MODES,
      composerMenuLabels: {
        attachFiles: 'Anexar arquivos',
        attachFilesDescription:
          'Envie documentos, imagens ou vídeos do computador.',
        mobilePhoto: 'Enviar foto do celular',
        mobilePhotoDescription:
          'Abra um QR Code para anexar mídia do telefone.',
        mobileUpload: 'Enviar do celular',
        mobileUploadDescription:
          'Escolha arquivos recentes enviados pelo aparelho.',
        signature: 'Ativar assinatura',
        participants: 'Selecionar participantes',
        participantsDescription: 'Inclua agentes acompanhando esta conversa.',
        ai: 'Sugerir resposta',
        aiDescription: 'Gere uma resposta curta somente quando precisar.',
        article: 'Buscar artigo',
        articleDescription: 'Insira um artigo da central de ajuda.',
        macros: 'Automações',
        macroDescription: 'Execute esta automação na conversa atual.',
        client: 'Cliente',
        team: 'Time',
        task: 'Tarefa',
      },
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      uiFlags: 'integrations/getUIFlags',
      macros: 'macros/getMacros',
      macrosUiFlags: 'macros/getUIFlags',
    }),
    wrapClass() {
      return {
        'is-note-mode': this.isNote,
      };
    },
    showAttachButton() {
      return this.showFileUpload || this.isNote;
    },
    showAudioRecorderButton() {
      if (this.isALineChannel) {
        return false;
      }
      // Disable audio recorder for safari browser as recording is not supported
      // const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(
      //   navigator.userAgent
      // );

      return (
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.VOICE_RECORDER
        ) && this.showAudioRecorder
        // !isSafari
      );
    },
    showAudioPlayStopButton() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isInstagramDM() {
      return this.conversationType === 'instagram_direct_message';
    },
    allowedFileTypes() {
      // Use default file types for private notes
      if (this.isOnPrivateNote) {
        return this.ALLOWED_FILE_TYPES;
      }

      let channelType = this.channelType || this.inbox?.channel_type;

      if (this.isAnInstagramChannel || this.isInstagramDM) {
        channelType = INBOX_TYPES.INSTAGRAM;
      }

      return getAllowedFileTypesByChannel({
        channelType,
        medium: this.inbox?.medium,
      });
    },
    enableDragAndDrop() {
      return !this.newConversationModalActive;
    },
    audioRecorderPlayStopIcon() {
      switch (this.recordingAudioState) {
        // playing paused recording stopped inactive destroyed
        case 'playing':
          return 'i-ph-pause';
        case 'paused':
          return 'i-ph-play';
        case 'stopped':
          return 'i-ph-play';
        default:
          return 'i-ph-stop';
      }
    },
    showMessageSignatureButton() {
      return !this.isOnPrivateNote;
    },
    sendWithSignature() {
      // channelType is sourced from inboxMixin
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    signatureToggleTooltip() {
      return this.sendWithSignature
        ? this.$t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
        : this.$t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP');
    },
    enableInsertArticleInReply() {
      return this.portalSlug;
    },
    isFetchingAppIntegrations() {
      return this.uiFlags.isFetching;
    },
    quotedReplyToggleTooltip() {
      return this.quotedReplyEnabled
        ? this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.DISABLE_TOOLTIP')
        : this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.ENABLE_TOOLTIP');
    },
    mobileUploadFavoriteStorageKey() {
      return `cw-mobile-upload-favorites-${this.accountId}-${this.conversationId}`;
    },
    mobileUploadDisplayedMedia() {
      return this.mobileUploadGalleryTab === 'favorites'
        ? this.mobileUploadFavorites
        : this.mobileUploadMedia;
    },
    hasMacros() {
      return (this.macros || []).length > 0;
    },
    replyTarget: {
      get() {
        if (this.replyMode === REPLY_EDITOR_MODES.NOTE) return 'team';
        if (this.replyMode === REPLY_EDITOR_MODES.TASK) return 'task';
        return 'client';
      },
      set(value) {
        const modeMap = {
          client: REPLY_EDITOR_MODES.REPLY,
          team: REPLY_EDITOR_MODES.NOTE,
          task: REPLY_EDITOR_MODES.TASK,
        };
        this.$emit('setReplyMode', modeMap[value] || REPLY_EDITOR_MODES.REPLY);
      },
    },
  },
  mounted() {
    ActiveStorage.start();
    this.$store.dispatch('macros/get');
  },
  methods: {
    hideMobileUploadModal() {
      this.isMobileUploadModalOpen = false;
    },
    toggleComposerMenu() {
      this.isComposerMenuOpen = !this.isComposerMenuOpen;
    },
    closeComposerMenu() {
      this.isComposerMenuOpen = false;
    },
    toggleTargetMenu() {
      this.isTargetMenuOpen = !this.isTargetMenuOpen;
    },
    closeTargetMenu() {
      this.isTargetMenuOpen = false;
    },
    setReplyTarget(value) {
      this.replyTarget = value;
      this.closeTargetMenu();
    },
    openMobileUploadAndCloseMenu() {
      this.closeComposerMenu();
      this.openMobileUploadModal();
    },
    toggleSignatureAndCloseMenu() {
      this.closeComposerMenu();
      this.toggleMessageSignature();
    },
    async executeMacroFromMenu(macro) {
      try {
        this.executingMacroId = macro.id;
        await this.$store.dispatch('macros/execute', {
          macroId: macro.id,
          conversationIds: [this.conversationId],
        });
        useTrack(CONVERSATION_EVENTS.EXECUTED_A_MACRO);
        useAlert(this.$t('MACROS.EXECUTE.EXECUTED_SUCCESSFULLY'));
        this.closeComposerMenu();
      } catch {
        useAlert(this.$t('MACROS.ERROR'));
      } finally {
        this.executingMacroId = null;
      }
    },
    async openMobileUploadModal() {
      this.isMobileUploadModalOpen = true;
      this.isFetchingMobileUploadLink = true;
      this.mobileUploadUrl = '';
      this.mobileUploadQrCodeUrl = '';
      this.mobileUploadFavorites = this.readMobileUploadFavorites();
      this.loadMobileUploadMedia();

      try {
        const { data } = await MessageApi.createMobileUploadLink(
          this.conversationId
        );
        this.mobileUploadUrl = data.url;
        this.mobileUploadQrCodeUrl = await QRCode.toDataURL(data.url, {
          width: 240,
          margin: 2,
          color: {
            dark: '#020617',
            light: '#FFFFFF',
          },
        });
      } catch (error) {
        useAlert(this.$t('CONVERSATION.REPLYBOX.MOBILE_UPLOAD.ERROR'));
        this.hideMobileUploadModal();
      } finally {
        this.isFetchingMobileUploadLink = false;
      }
    },
    async loadMobileUploadMedia() {
      this.isFetchingMobileUploadMedia = true;

      try {
        const { data } = await ConversationApi.getAllAttachments(
          this.conversationId
        );
        this.mobileUploadMedia = (data.payload || [])
          .filter(media => ['image', 'video'].includes(media.file_type))
          .slice(0, 12);
      } catch (error) {
        this.mobileUploadMedia = [];
      } finally {
        this.isFetchingMobileUploadMedia = false;
      }
    },
    mobileUploadMediaKey(media) {
      return `${media.message_id}-${media.data_url || media.thumb_url}`;
    },
    readMobileUploadFavorites() {
      try {
        return JSON.parse(
          window.localStorage.getItem(this.mobileUploadFavoriteStorageKey) ||
            '[]'
        );
      } catch (error) {
        return [];
      }
    },
    persistMobileUploadFavorites() {
      window.localStorage.setItem(
        this.mobileUploadFavoriteStorageKey,
        JSON.stringify(this.mobileUploadFavorites.slice(0, 30))
      );
    },
    isMobileUploadFavorite(media) {
      const mediaKey = this.mobileUploadMediaKey(media);
      return this.mobileUploadFavorites.some(
        favorite => this.mobileUploadMediaKey(favorite) === mediaKey
      );
    },
    toggleMobileUploadFavorite(media) {
      const mediaKey = this.mobileUploadMediaKey(media);
      if (this.isMobileUploadFavorite(media)) {
        this.mobileUploadFavorites = this.mobileUploadFavorites.filter(
          favorite => this.mobileUploadMediaKey(favorite) !== mediaKey
        );
      } else {
        this.mobileUploadFavorites = [media, ...this.mobileUploadFavorites];
      }
      this.persistMobileUploadFavorites();
    },
    mobileUploadFileName(media, blob) {
      const url = media.data_url || media.thumb_url || '';
      const fileName = decodeURIComponent(
        url.split('?')[0].split('/').filter(Boolean).pop() || ''
      );
      if (fileName.includes('.')) {
        return fileName;
      }

      const extension = media.extension || blob.type.split('/')[1] || 'file';
      return `mobile-upload-${media.message_id}.${extension}`;
    },
    async attachMobileUploadMedia(media) {
      const mediaUrl = media.data_url || media.thumb_url;
      if (!mediaUrl) return;

      this.isAttachingMobileUploadMedia = true;
      try {
        const response = await fetch(mediaUrl, { credentials: 'include' });
        if (!response.ok) {
          throw new Error('Could not fetch media');
        }
        const blob = await response.blob();
        const file = new File([blob], this.mobileUploadFileName(media, blob), {
          type: blob.type,
        });
        this.onFileUpload({
          name: file.name,
          type: file.type,
          size: file.size,
          file,
        });
        this.hideMobileUploadModal();
      } catch (error) {
        useAlert(this.$t('CONVERSATION.REPLYBOX.MOBILE_UPLOAD.ATTACH_ERROR'));
      } finally {
        this.isAttachingMobileUploadMedia = false;
      }
    },
    toggleMessageSignature() {
      this.setSignatureFlagForInbox(this.channelType, !this.sendWithSignature);
    },
    replaceText(text) {
      this.$emit('replaceText', text);
    },
    toggleInsertArticle() {
      this.$emit('toggleInsertArticle');
    },
    suggestReplyAndCloseMenu() {
      this.closeComposerMenu();
      this.$emit('suggestReply');
    },
  },
};
</script>

<template>
  <div class="composer-actions" :class="wrapClass">
    <div v-on-clickaway="closeComposerMenu" class="composer-menu-anchor">
      <button
        type="button"
        class="composer-icon-button composer-plus-button"
        :class="{ 'is-open': isComposerMenuOpen }"
        @click="toggleComposerMenu"
      >
        <span class="i-lucide-plus size-5" />
      </button>
      <div v-if="isComposerMenuOpen" class="composer-menu">
        <FileUpload
          ref="uploadRef"
          input-id="conversationAttachment"
          :size="4096 * 4096"
          :accept="allowedFileTypes"
          :multiple="enableMultipleFileUpload"
          :drop="enableDragAndDrop"
          :drop-directory="false"
          :data="{
            direct_upload_url: '/rails/active_storage/direct_uploads',
            direct_upload: true,
          }"
          @input-file="onFileUpload"
        >
          <button
            v-if="showAttachButton"
            type="button"
            class="composer-menu-item"
            @click="closeComposerMenu"
          >
            <span
              class="composer-menu-icon text-n-blue-text i-lucide-paperclip"
            />
            <span class="min-w-0">
              <strong>{{ composerMenuLabels.attachFiles }}</strong>
              <small>{{ composerMenuLabels.attachFilesDescription }}</small>
            </span>
          </button>
        </FileUpload>
        <button
          v-if="showAttachButton"
          type="button"
          class="composer-menu-item"
          @click="openMobileUploadAndCloseMenu"
        >
          <span class="composer-menu-icon text-n-ruby-9 i-lucide-camera" />
          <span class="min-w-0">
            <strong>{{ composerMenuLabels.mobilePhoto }}</strong>
            <small>{{ composerMenuLabels.mobilePhotoDescription }}</small>
          </span>
        </button>
        <button
          v-if="showAttachButton"
          type="button"
          class="composer-menu-item"
          @click="openMobileUploadAndCloseMenu"
        >
          <span class="composer-menu-icon text-n-iris-9 i-lucide-smartphone" />
          <span class="min-w-0">
            <strong>{{ composerMenuLabels.mobileUpload }}</strong>
            <small>{{ composerMenuLabels.mobileUploadDescription }}</small>
          </span>
        </button>
        <button
          v-if="showMessageSignatureButton"
          type="button"
          class="composer-menu-item"
          @click="toggleSignatureAndCloseMenu"
        >
          <span class="composer-menu-icon text-n-amber-10 i-lucide-signature" />
          <span class="min-w-0">
            <strong>{{ composerMenuLabels.signature }}</strong>
            <small>{{ signatureToggleTooltip }}</small>
          </span>
        </button>
        <ConversationParticipant :conversation-id="conversationId" />
        <button
          type="button"
          class="composer-menu-item"
          @click="suggestReplyAndCloseMenu"
        >
          <span class="composer-menu-icon text-n-purple-9 i-lucide-sparkles" />
          <span class="min-w-0">
            <strong>{{ composerMenuLabels.ai }}</strong>
            <small>{{ composerMenuLabels.aiDescription }}</small>
          </span>
        </button>
        <button
          v-if="enableInsertArticleInReply"
          type="button"
          class="composer-menu-item"
          @click="toggleInsertArticle"
        >
          <span class="composer-menu-icon text-n-slate-11 i-lucide-book-open" />
          <span class="min-w-0">
            <strong>{{ composerMenuLabels.article }}</strong>
            <small>{{ composerMenuLabels.articleDescription }}</small>
          </span>
        </button>
        <template v-if="macrosUiFlags.isFetching || hasMacros">
          <div class="composer-menu-divider" />
          <div
            class="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
          >
            {{ composerMenuLabels.macros }}
          </div>
          <div
            v-if="macrosUiFlags.isFetching"
            class="px-3 py-2 text-sm text-n-slate-11"
          >
            {{ $t('MACROS.LOADING') }}
          </div>
          <template v-else>
            <button
              v-for="macro in macros"
              :key="macro.id"
              type="button"
              class="composer-menu-item"
              :disabled="executingMacroId === macro.id"
              @click="executeMacroFromMenu(macro)"
            >
              <span class="composer-menu-icon text-n-amber-10 i-lucide-bolt" />
              <span class="min-w-0">
                <strong>{{ macro.name }}</strong>
                <small>{{ composerMenuLabels.macroDescription }}</small>
              </span>
            </button>
          </template>
        </template>
      </div>
    </div>

    <div class="composer-right-actions">
      <div v-on-clickaway="closeTargetMenu" class="composer-target-wrap">
        <button
          type="button"
          class="composer-target-button"
          @click="toggleTargetMenu"
        >
          <span>
            {{
              replyTarget === 'team'
                ? composerMenuLabels.team
                : replyTarget === 'task'
                  ? composerMenuLabels.task
                  : composerMenuLabels.client
            }}
          </span>
          <span class="i-lucide-chevron-down size-4 text-n-slate-10" />
        </button>
        <div v-if="isTargetMenuOpen" class="composer-target-menu">
          <button
            type="button"
            class="composer-target-option"
            :class="{ 'is-active': replyTarget === 'client' }"
            @click="setReplyTarget('client')"
          >
            {{ composerMenuLabels.client }}
          </button>
          <button
            type="button"
            class="composer-target-option"
            :class="{ 'is-active': replyTarget === 'team' }"
            @click="setReplyTarget('team')"
          >
            {{ composerMenuLabels.team }}
          </button>
          <button
            type="button"
            class="composer-target-option"
            :class="{ 'is-active': replyTarget === 'task' }"
            @click="setReplyTarget('task')"
          >
            {{ composerMenuLabels.task }}
          </button>
        </div>
      </div>

      <button
        v-if="showAudioRecorder || showAudioRecorderButton"
        type="button"
        class="composer-icon-button"
        @click="toggleAudioRecorder"
      >
        <span
          :class="!isRecordingAudio ? 'i-lucide-mic' : 'i-lucide-mic-off'"
          class="size-5"
        />
      </button>
      <button
        v-if="showAudioPlayStopButton"
        type="button"
        class="composer-audio-state"
        @click="toggleAudioRecorderPlayPause"
      >
        <span :class="audioRecorderPlayStopIcon" />
        {{ recordingAudioDurationText }}
      </button>
      <button
        type="button"
        class="composer-send-button"
        :disabled="isSendDisabled"
        :title="sendButtonText"
        @click="onSend"
      >
        <span class="i-lucide-arrow-up size-5" />
      </button>
    </div>

    <Teleport to="body">
      <woot-modal
        v-model:show="isMobileUploadModalOpen"
        :on-close="hideMobileUploadModal"
      >
        <div class="flex flex-col items-center gap-4 p-6 text-center">
          <div>
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ $t('CONVERSATION.REPLYBOX.MOBILE_UPLOAD.TITLE') }}
            </h3>
            <p class="mt-2 max-w-sm text-sm leading-5 text-n-slate-11">
              {{ $t('CONVERSATION.REPLYBOX.MOBILE_UPLOAD.DESCRIPTION') }}
            </p>
          </div>
          <div
            class="flex h-64 w-64 items-center justify-center rounded-lg border border-n-weak bg-white p-3 dark:bg-n-solid-2"
          >
            <span
              v-if="isFetchingMobileUploadLink"
              class="text-sm text-n-slate-11"
            >
              {{ $t('CONVERSATION.REPLYBOX.MOBILE_UPLOAD.LOADING') }}
            </span>
            <img
              v-else-if="mobileUploadQrCodeUrl"
              :src="mobileUploadQrCodeUrl"
              :alt="$t('CONVERSATION.REPLYBOX.MOBILE_UPLOAD.LINK_LABEL')"
              class="h-full w-full"
            />
          </div>
          <a
            v-if="mobileUploadUrl"
            :href="mobileUploadUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="max-w-full truncate text-sm font-medium text-n-blue-text hover:underline"
          >
            {{ mobileUploadUrl }}
          </a>
          <div class="w-full max-w-lg pt-2 text-left">
            <div
              class="mb-3 flex items-center justify-between gap-3 border-b border-n-weak"
            >
              <div class="flex gap-1">
                <button
                  type="button"
                  class="px-3 py-2 text-sm font-medium"
                  :class="
                    mobileUploadGalleryTab === 'recent'
                      ? 'border-b-2 border-n-blue-text text-n-blue-text'
                      : 'text-n-slate-11'
                  "
                  @click="mobileUploadGalleryTab = 'recent'"
                >
                  {{ $t('CONVERSATION.REPLYBOX.MOBILE_UPLOAD.RECENT') }}
                </button>
                <button
                  type="button"
                  class="px-3 py-2 text-sm font-medium"
                  :class="
                    mobileUploadGalleryTab === 'favorites'
                      ? 'border-b-2 border-n-blue-text text-n-blue-text'
                      : 'text-n-slate-11'
                  "
                  @click="mobileUploadGalleryTab = 'favorites'"
                >
                  {{ $t('CONVERSATION.REPLYBOX.MOBILE_UPLOAD.FAVORITES') }}
                </button>
              </div>
              <span
                v-if="isFetchingMobileUploadMedia"
                class="text-xs text-n-slate-10"
              >
                {{ $t('CONVERSATION.REPLYBOX.MOBILE_UPLOAD.LOADING_MEDIA') }}
              </span>
            </div>
            <div
              v-if="mobileUploadDisplayedMedia.length"
              class="grid grid-cols-3 gap-2"
            >
              <div
                v-for="media in mobileUploadDisplayedMedia"
                :key="mobileUploadMediaKey(media)"
                class="group relative aspect-square overflow-hidden rounded-md border border-n-weak bg-n-slate-2"
              >
                <img
                  v-if="media.file_type === 'image'"
                  :src="media.thumb_url || media.data_url"
                  :alt="$t('CONVERSATION.REPLYBOX.MOBILE_UPLOAD.MEDIA_ALT')"
                  class="h-full w-full object-cover"
                />
                <div
                  v-else
                  class="flex h-full w-full items-center justify-center bg-n-slate-3 text-n-slate-11"
                >
                  <span class="i-ph-video-camera text-2xl" />
                </div>
                <button
                  type="button"
                  class="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-n-slate-12 shadow-sm dark:bg-n-solid-2/90"
                  :aria-label="
                    $t('CONVERSATION.REPLYBOX.MOBILE_UPLOAD.FAVORITE')
                  "
                  @click="toggleMobileUploadFavorite(media)"
                >
                  <span
                    :class="
                      isMobileUploadFavorite(media)
                        ? 'i-ph-star-fill text-n-amber-9'
                        : 'i-ph-star'
                    "
                  />
                </button>
                <button
                  type="button"
                  class="absolute inset-x-1 bottom-1 flex items-center justify-center gap-1 rounded bg-n-slate-12/90 px-2 py-1 text-xs font-medium text-white opacity-0 transition-opacity group-hover:opacity-100"
                  :disabled="isAttachingMobileUploadMedia"
                  @click="attachMobileUploadMedia(media)"
                >
                  <span class="i-ph-paper-plane-tilt" />
                  {{ $t('CONVERSATION.REPLYBOX.MOBILE_UPLOAD.ATTACH') }}
                </button>
              </div>
            </div>
            <p v-else class="py-4 text-center text-sm text-n-slate-11">
              {{
                mobileUploadGalleryTab === 'favorites'
                  ? $t('CONVERSATION.REPLYBOX.MOBILE_UPLOAD.NO_FAVORITES')
                  : $t('CONVERSATION.REPLYBOX.MOBILE_UPLOAD.NO_RECENT')
              }}
            </p>
          </div>
        </div>
      </woot-modal>
    </Teleport>
    <transition name="modal-fade">
      <div
        v-show="uploadRef && uploadRef.dropActive"
        class="flex fixed top-0 right-0 bottom-0 left-0 z-20 flex-col gap-2 justify-center items-center w-full h-full text-n-slate-12 bg-modal-backdrop-light dark:bg-modal-backdrop-dark"
      >
        <fluent-icon icon="cloud-backup" size="40" />
        <h4 class="text-2xl break-words text-n-slate-12">
          {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
        </h4>
      </div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
.composer-actions {
  @apply pointer-events-none absolute inset-x-0 bottom-0 z-[100000] h-[4.25rem];
}

.composer-actions > * {
  @apply pointer-events-auto;
}

.composer-menu-anchor {
  @apply absolute left-5 top-1/2 z-[100001] flex shrink-0 -translate-y-1/2 items-center;
}

.composer-icon-button {
  @apply grid size-9 shrink-0 place-content-center rounded-full border-0 bg-transparent text-n-slate-11 transition hover:bg-n-alpha-2 hover:text-n-slate-12 disabled:cursor-not-allowed disabled:opacity-50;
}

.composer-plus-button.is-open {
  @apply rotate-45 bg-n-alpha-2 text-n-slate-12;
}

.composer-menu {
  @apply absolute bottom-12 left-0 z-[100002] w-[min(34rem,calc(100vw-8rem))] overflow-visible rounded-2xl border border-n-weak bg-white/95 p-2 backdrop-blur-xl dark:bg-n-solid-2/95;
  box-shadow: 0 24px 64px rgb(2 6 23 / 15%);
}

.composer-menu-item {
  @apply flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-n-slate-12 transition hover:bg-n-alpha-1 disabled:cursor-wait disabled:opacity-60;

  strong {
    @apply block truncate text-sm font-semibold leading-5;
  }

  small {
    @apply block truncate text-xs leading-4 text-n-slate-10;
  }
}

.composer-menu-icon {
  @apply grid size-5 shrink-0 place-content-center text-sm;
}

.composer-menu-divider {
  @apply my-1 h-px bg-n-weak;
}

.composer-right-actions {
  @apply absolute right-4 top-1/2 z-[100001] flex -translate-y-1/2 items-center gap-1.5;
}

.composer-target-wrap {
  @apply relative shrink-0;
}

.composer-target-button {
  @apply flex h-9 w-28 items-center justify-center gap-1 rounded-full border border-transparent bg-transparent px-2 text-base font-medium text-n-slate-11 outline-none transition hover:bg-n-alpha-2 focus:bg-n-alpha-2;
}

.composer-target-menu {
  @apply absolute bottom-11 right-0 z-[99999] w-36 overflow-hidden rounded-2xl border border-n-weak bg-white/95 p-1.5 backdrop-blur-xl dark:bg-n-solid-2/95;
  box-shadow: 0 18px 48px rgb(2 6 23 / 14%);
}

.composer-target-option {
  @apply flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm font-medium text-n-slate-11 transition hover:bg-n-alpha-1;
}

.composer-target-option.is-active {
  @apply bg-n-alpha-2 text-n-slate-12;
}

.composer-audio-state {
  @apply flex h-9 shrink-0 items-center gap-1 rounded-full bg-n-alpha-2 px-3 text-sm font-medium text-n-slate-11;
}

.composer-send-button {
  @apply grid size-9 shrink-0 place-content-center rounded-full bg-n-brand text-white shadow-sm transition hover:brightness-110 disabled:cursor-not-allowed disabled:bg-n-slate-5 disabled:text-n-slate-9 disabled:shadow-none;
}

::v-deep .file-uploads {
  label {
    @apply w-full cursor-pointer;
  }
}
</style>
